import type React from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Button } from '@/components/atoms/Buttons';
import { Card, CardHeader } from '@/components/atoms/Card';
import Checkbox from '@/components/atoms/Checkbox';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import TextField from '@/components/atoms/TextField';
import { ModalBody, ModalFooter } from '@/components/molecules/ModalComponent';

import styles from './UserInviteForm.module.scss';

export interface UserInviteFormState {
    role: string;
    /** Unset for an OptoScale admin, who belongs to no client. */
    clientId: number | undefined;
    locationIds: number[];
    email: string;
}

/** Minimal client shape needed by the invite form; only id is used at runtime. */
export interface ExtendedClientInfo {
    id: number;
    role?: string;
    authLicenses?: string[];
}

interface UserInviteFormProps {
    canInviteForOptoscaleAdmin: boolean;
    canInviteForClient: boolean;
    client?: ExtendedClientInfo;
    clients: {
        id: number;
        name: string;
        locations: {
            id: number;
            name: string;
        }[];
    }[];
    onSubmit: (state: UserInviteFormState) => Promise<unknown>;
    /** Renders the form as a modal's body and footer instead of a page box. */
    hideBox?: boolean;
    /** Shown as Cancel beside the submit button in the modal footer. */
    onCancel?: () => void;
}

export function UserInviteForm({
    client,
    canInviteForOptoscaleAdmin,
    clients,
    canInviteForClient,
    onSubmit,
    hideBox = false,
    onCancel,
}: UserInviteFormProps) {
    const getDefaultClientId = () => {
        return client ? client.id : undefined;
    };

    const { t } = useTranslation();

    const [state, setState] = useState<UserInviteFormState & { confirmEmail: string }>({
        confirmEmail: '',
        email: '',
        clientId: getDefaultClientId(),
        locationIds: [],
        role: canInviteForOptoscaleAdmin
            ? 'OptoScaleAdmin'
            : canInviteForClient
              ? 'ClientAdmin'
              : 'LocationAdmin',
    });
    const [success, setSuccess] = useState<{ user: string } | null>(null);

    const [disabled, setDisabled] = useState(false);

    const updateState = (partialState: Partial<typeof state>) =>
        setState((s) => ({ ...s, ...partialState }));

    const submit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        try {
            e.preventDefault();
            setDisabled(true);
            setSuccess(null);
            setError(null);

            await onSubmit(state);

            // Only show success message if not in modal (hideBox is false)
            if (!hideBox) {
                setSuccess({ user: state.email });
                updateState({
                    email: '',
                    confirmEmail: '',
                });
            } else {
                // Reset form when used in modal (success is shown via toast)
                updateState({
                    email: '',
                    confirmEmail: '',
                });
            }
        } catch (e) {
            // Only show error in form if not in modal (modal will show toast)
            if (!hideBox) {
                setError(apiErrorContent(e, t('common.labels.somethingHappened')));
                // Don't re-throw - submit is used as an event handler where the promise is never awaited.
                // Re-throwing would create an unhandled promise rejection. The error is already
                // displayed to the user via setError above.
            }
            // When in modal mode, don't re-throw - parent's handleInviteUser already handles errors
            // Re-throwing would create an unhandled promise rejection since the parent's
            // try-catch already caught and handled the error with a toast notification
        } finally {
            setDisabled(false);
        }
    };
    const emailNotSet = state.email === '';
    const confirmEmailNotEqualToEmail = state.email !== state.confirmEmail;
    const clientsNotSet =
        (state.role === 'ClientAdmin' || state.role === 'LocationAdmin') &&
        state.clientId === undefined;
    const locationsNotSet = state.role === 'LocationAdmin' && state.locationIds.length === 0;
    const invalid = emailNotSet || confirmEmailNotEqualToEmail || clientsNotSet || locationsNotSet;

    const toggleLocationId = (id: number) => {
        const current = state.locationIds;
        const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
        updateState({ locationIds: next });
    };

    const selectedClient = clients.find((client) => client.id === state.clientId);

    const [error, setError] = useState<React.ReactNode>(null);
    const locationsLabelId = useId();

    const fields = (
        <>
            <FormField label={t('userAccess.common.role')}>
                <Select
                    required
                    name="role"
                    value={state.role}
                    onChange={(event) => {
                        event.preventDefault();
                        updateState({
                            role: event.target.value,
                            clientId: getDefaultClientId(),
                            locationIds: [],
                        });
                    }}>
                    {canInviteForOptoscaleAdmin && (
                        <option value="OptoScaleAdmin">
                            {t('userAccess.common.optoscaleAdmin')}
                        </option>
                    )}
                    {canInviteForClient && (
                        <option value="ClientAdmin">{t('userAccess.common.clientAdmin')}</option>
                    )}

                    <option value="LocationAdmin">{t('userAccess.common.locationAdmin')}</option>
                </Select>
            </FormField>
            {(state.role === 'ClientAdmin' || state.role === 'LocationAdmin') && (
                <FormField label={t('common.labels.client')}>
                    <Select
                        required
                        name="clientId"
                        value={state.clientId ?? ''}
                        disabled={client ? true : false}
                        onChange={(e) => {
                            updateState({
                                // The blank option clears the choice instead of storing NaN.
                                clientId:
                                    e.target.value === ''
                                        ? undefined
                                        : Number.parseInt(e.target.value, 10),
                                locationIds: [],
                            });
                        }}>
                        <option />
                        {clients.map((client) => (
                            <option key={client.id} value={client.id}>
                                {client.name}
                            </option>
                        ))}
                    </Select>
                </FormField>
            )}
            {state.role === 'LocationAdmin' && selectedClient && (
                <div className={styles.locationField}>
                    <span id={locationsLabelId} className={styles.locationLabel}>
                        {t('common.labels.location')}
                        {state.locationIds.length > 0 && (
                            <span className={styles.locationCount}>
                                (
                                {t('userAccess.nOfM', {
                                    n: state.locationIds.length,
                                    m: selectedClient.locations.length,
                                })}
                                )
                            </span>
                        )}
                    </span>
                    <div
                        role="group"
                        aria-labelledby={locationsLabelId}
                        className={styles.locationList}>
                        {selectedClient.locations.map((location) => (
                            <Checkbox
                                key={location.id}
                                label={location.name}
                                disabled={disabled}
                                checked={state.locationIds.includes(location.id)}
                                onCheckedChange={() => toggleLocationId(location.id)}
                            />
                        ))}
                    </div>
                </div>
            )}
            <FormField label={t('userAccess.common.eMail')}>
                <TextField
                    required
                    name="email"
                    placeholder={t('userAccess.common.eMail')}
                    type="email"
                    value={state.email}
                    onChange={(event) => {
                        event.preventDefault();
                        updateState({
                            email: event.target.value,
                        });
                    }}
                />
            </FormField>
            <FormField label={t('userAccess.userInviteForm.confirmEMail')}>
                <TextField
                    required
                    name="confirmEmail"
                    placeholder={t('userAccess.userInviteForm.confirmEMail')}
                    value={state.confirmEmail}
                    invalid={confirmEmailNotEqualToEmail}
                    type="email"
                    onChange={(event) => {
                        event.preventDefault();
                        updateState({
                            confirmEmail: event.target.value,
                        });
                    }}
                />
            </FormField>
            {error && <Alert tone="danger">{error}</Alert>}
        </>
    );

    const submitButton = (
        <Button type="submit" variant="primary" trackingId="user_invite_submit" disabled={invalid}>
            {t('userAccess.common.inviteUser')}
        </Button>
    );

    // In a modal the actions sit in the footer like every other modal's: Cancel, then submit.
    if (hideBox) {
        return (
            <form onSubmit={submit}>
                <ModalBody>{fields}</ModalBody>
                <ModalFooter>
                    {onCancel && (
                        <Button type="button" variant="secondary" onClick={onCancel}>
                            {t('common.actions.cancel')}
                        </Button>
                    )}
                    {submitButton}
                </ModalFooter>
            </form>
        );
    }

    return (
        <Card
            as="section"
            padding="xl"
            header={<CardHeader title={t('userAccess.common.inviteUser')} level="h2" />}>
            <form onSubmit={submit}>
                {fields}
                {submitButton}
                {success && (
                    <Alert tone="success">
                        {t('userAccess.common.userInvited', { user: success.user })}
                    </Alert>
                )}
            </form>
        </Card>
    );
}

export default UserInviteForm;
